import { useEffect, useState, useMemo, useRef, useContext } from 'react';
import { useTheme } from '../components/ThemeContext';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from '../components/CheckoutForm';
import StockService from '../Api_Module/Api_Services/StockService';
import AuthService from '../Api_Module/Api_Services/AuthService';
import { buildGallery, getAlt, getCaption } from '../utility/galleryMeta';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { UserContext } from '../context/UserContext';
import Analytics from '../services/analytics';

interface PricingProps {
  openPopup?: () => void;
}

export function Pricing({ openPopup }: PricingProps = {}) {
  const { theme } = useTheme();
  const userContext = useContext(UserContext);

  // Get user data from context instead of local state
  const { userData, refreshUser } = userContext || {};

  const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY || '');

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const [subscriptionConfig, setSubscriptionConfig] = useState<any>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('STOCK_AUTH_TOKEN');
      if (!token) {
        setIsAuth(false);
        setIsLoading(false);
        return;
      }
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        const isTokenValid = decoded.exp > Date.now() / 1000;
        setIsAuth(isTokenValid);

        // Refresh user data from context
        if (isTokenValid && refreshUser) {
          await refreshUser();
        }
      } catch (e) {
        console.error('Auth check error:', e);
        setIsAuth(false);
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const [billing, setBilling] = useState<'month' | 'year'>('month');
  const [showCheckout, setShowCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [discount, setDiscount] = useState<number | null>(null);
  const [price, setPrice] = useState<any>(null);
  const [afterDiscountPrice, setAfterDiscountPrice] = useState<any>(null);

  const isYearly = billing === 'year';

  // Fallback features if Stripe metadata does not provide them
  const PLAN_FEATURES = [
    'Premium Stock Analysis',
    'Fund portfolio stats',
    'Top buys and sells',
    'Fund Portfolio filters',
    'Common fund bets',
    'Large fund trades',
    'Fund trend visualizations',
    'Top buyers/sellers',
    'Historical holders data',
    'Real-time Institutional Portfolio Tracking',
    'No ads.',
    'SWOT Analysis',
    'Analyst consensus picks',
    'Analyst updates feed',
    'Stock specific price targets',
    'All news sources',
    'Build your own projections'
  ];

  // Accept features as JSON string, pipe/comma separated, or array
  const parseFeatures = (val: any): string[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(String);
    if (typeof val === 'string') {
      try {
        const arr = JSON.parse(val);
        if (Array.isArray(arr)) return arr.map(String);
      } catch { }
      return val.split(/[|,]/).map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Features used in the checkout modal for the currently selected product
  const modalFeatures = useMemo(() => {
    const direct = Array.isArray(selectedProduct?.features) ? (selectedProduct?.features as string[]) : [];
    const parsed = parseFeatures(selectedProduct?.metadata?.features);
    return direct.length ? direct : (parsed.length ? parsed : PLAN_FEATURES);
  }, [selectedProduct]);

  useEffect(() => {
    getProducts();
    getSubscriptionConfig();
  }, []);

  // Shared gallery and carousel state
  const galleryImages = useMemo(() => buildGallery(), []);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      if (idx !== currentSlide) setCurrentSlide(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true } as any);
    return () => el.removeEventListener('scroll', onScroll as any);
  }, [currentSlide]);

  const goTo = (index: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const clamp = Math.max(0, Math.min(index, galleryImages.length - 1));
    el.scrollTo({ left: clamp * el.clientWidth, behavior: 'smooth' as ScrollBehavior });
    setCurrentSlide(clamp);
  };
  const next = () => goTo(currentSlide + 1);
  const prev = () => goTo(currentSlide - 1);

  const getSubscriptionConfig = async () => {
    try {
      const response = await AuthService.getSubscriptionConfig();
      if (response) {
        setSubscriptionConfig(response);
      }
    } catch (error) {
      console.error('Error fetching subscription config:', error);
    }
  };

  const getProducts = async () => {
    try {
      const response = await StockService.getProducts();
      if (response) {
        setProducts(response);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const appearance: any = {
    theme: 'stripe',
    labels: 'floating',
    variables: {
      colorPrimary: '#0570de',
      colorBackground: '#ffffff',
      colorText: '#30313d',
      colorDanger: '#df1b41',
      fontFamily: 'Ideal Sans, system-ui, sans-serif',
      spacingUnit: '3px',
      borderRadius: '4px',
    }
  };

  const fetchClientSecret = async () => {
    try {
      if (!selectedProduct) return;

      // Return existing clientSecret if already fetched
      if (clientSecret) {
        return clientSecret;
      }
      setClientSecret(null);
      setDiscount(null);
      setPrice(null);
      setAfterDiscountPrice(null);

      setIsLoading(true);
      const response = await StockService.getCheckout({
        priceId: selectedProduct?.price?.id,
        billingCycle: billing,
        successUrl: window.location.origin + '/subscription/success',
      });
      if (response) {
        const { checkoutSessionClientSecret, checkoutUrl, discount, price, afterDiscountPrice } = response;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        }
        if (checkoutSessionClientSecret) {
          setClientSecret(checkoutSessionClientSecret);
          setDiscount(discount || 0);
          setPrice(price || 0);
          setAfterDiscountPrice(afterDiscountPrice || 0);
          return checkoutSessionClientSecret;
        }
      }
    } catch (error) {
      console.error('Error fetching client secret:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscription = async (product: any) => {
    console.log('Selected product:', product);

    // Check if user is authenticated using the local state instead of UserContext
    if (!isAuth) {
      if (openPopup) {
        openPopup();
      }
      return;
    }

    // Track InitiateCheckout event
    Analytics.event('InitiateCheckout', {
      currency: 'USD',
      value: isYearly ? (product.price?.unit_amount_decimal * 12) : product.price?.unit_amount_decimal,
      content_name: product.name,
      content_ids: [product.id],
      num_items: 1,
      payment_plan: billing // 'month' or 'year'
    });

    setSelectedProduct(product);
    setClientSecret(null);
    setShowCheckout(true);
  };

  // Helper function to determine button state and text
  const getButtonState = (product: any) => {
    if (!userData) {
      return { text: 'Subscribe Now', disabled: false, isUpgrade: false };
    }

    const isTrialing = userData.subscriptionStatus === 'trialing' && userData.isTrial && userData.planActive;
    const hasActiveSubscription = userData.subscriptionStatus === 'active' && userData.planActive;
    const currentPlanInterval = userData.planInterval; // assuming this exists in userData
    const productInterval = product?.price?.recurring?.interval;

    if (hasActiveSubscription) {
      if (currentPlanInterval === productInterval) {
        return { text: 'Subscribe Now', disabled: true, isUpgrade: false };
      } else {
        return { text: 'Upgrade Plan', disabled: false, isUpgrade: true };
      }
    }

    if (isTrialing) {
      return { text: 'Upgrade Plan', disabled: false, isUpgrade: true };
    }

    return { text: 'Subscribe Now', disabled: false, isUpgrade: false };
  };

  return <div className="flex flex-col min-h-screen bg-theme-primary">
    {/* Hero Section */}
    <section className={`relative py-20 ${theme === 'dark' ? 'bg-gradient-to-br from-blue-900 to-gray-900 text-theme-primary' : 'bg-gradient-to-br from-blue-100 to-gray-100 text-gray-900'}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-0 right-0 w-1/3 h-1/3 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2 ${theme === 'dark' ? 'bg-blue-600/10' : 'bg-blue-400/20'}`}></div>
        <div className={`absolute bottom-0 left-0 w-1/2 h-1/2 rounded-full blur-3xl transform translate-x-1/4 translate-y-1/4 ${theme === 'dark' ? 'bg-green-600/10' : 'bg-green-400/20'}`}></div>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 uppercase tracking-wide">
            Affordable Simple Pricing
          </h1>
          <p className="text-lg text-theme-secondary">
            Choose the plan that works best for you
          </p>
        </div>
      </div>
    </section>

    {/* Pricing Summary Cards (moved up) */}
    <section className="py-14 md:py-20 bg-theme-primary">
      <div className="container mx-auto px-4">
        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center rounded-md p-1">
            <button
              onClick={() => setBilling('month')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 btn ${!isYearly
                ? 'active'
                : 'border-none'
                }`}
            >
              <span>Monthly</span>
              {subscriptionConfig?.monthCouponDisplay || (userData && userData.appliedMonthDiscount != 0) &&
                <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${isYearly ? 'bg-white/20 text-theme-primary' : 'bg-green-100 text-green-700'
                  }`}>
                  Save {subscriptionConfig?.monthCouponDisplay || ''} {userData && userData.appliedMonthDiscount ? (`${userData.appliedMonthDiscount}%`) : null}
                </span>}
            </button>
            <button
              onClick={() => setBilling('year')}
              className={`ml-1 px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 btn ${isYearly
                ? 'active'
                : 'border-none'
                }`}
            >
              <span>Yearly</span>
              {(subscriptionConfig?.yearCouponDisplay || (userData && userData.appliedYearDiscount != 0)) &&
                <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${isYearly ? 'bg-white/20 text-theme-primary' : 'bg-green-100 text-green-700'
                  }`}>
                  Save {parseFloat(subscriptionConfig?.yearCouponDisplay.replace('%', '') || '0') + parseFloat(String(userData?.appliedYearDiscount ?? '0'))}%
                </span>}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:gap-8 max-w-md mx-auto">
          {(products && products.length > 0 ? products : [
            {
              name: 'Unlimited Plan',
              description: 'Unlimited Stock',
              featured: true,
              features: PLAN_FEATURES,
              price: { unit_amount_decimal: 9.99, id: 'price_1N3mY2FgJusLR2NWWXrX4W8e', recurring: { interval: 'month' } },
            },
            {
              name: 'Unlimited Plan',
              description: 'Unlimited Stock',
              featured: true,
              features: PLAN_FEATURES,
              price: { unit_amount_decimal: 4.99, id: 'price_year_placeholder', recurring: { interval: 'year' } },
            }
          ]).filter((c: any) => c?.price?.recurring?.interval === billing).map((c: any, i: number) => {
            // Calculate pricing based on billing cycle and config
            const monthlyPrice = c?.price?.unit_amount_decimal || 9.99;

            // Get discount values from config
            const yearDiscount = subscriptionConfig?.yearCouponActive ? (subscriptionConfig?.yearCouponValue || 0) : 0;
            const monthDiscount = subscriptionConfig?.monthCouponActive ? (subscriptionConfig?.monthCouponValue || 0) : 0;
            //
            const userMonthDiscount = userData?.appliedMonthDiscount || 0;
            const userYearDiscount = userData?.appliedYearDiscount || 0;
            // Calculate discounted prices
            const yearlyDiscountMultiplier = (100 - (yearDiscount + userYearDiscount)) / 100;
            const monthlyDiscountMultiplier = (100 - (monthDiscount + userMonthDiscount)) / 100;
            console.log('yearlyDiscountMultiplier:', yearlyDiscountMultiplier, 'monthlyDiscountMultiplier:', monthlyDiscountMultiplier);

            const yearlyPrice = monthlyPrice * yearlyDiscountMultiplier;
            const discountedMonthlyPrice = monthlyPrice * monthlyDiscountMultiplier;

            const displayPriceFormatted = isYearly ? yearlyPrice.toFixed(2) : discountedMonthlyPrice.toFixed(2);

            const baseCard = c?.featured
              ? `${theme === 'dark' ? 'bg-black text-theme-primary ring-1 ring-blue-500' : 'bg-white text-gray-900 ring-1 ring-yellow-500'} shadow-xl `
              : 'bg-theme-secondary text-theme-primary border border-theme-tertiary shadow-lg';
            // Prefer features from Stripe metadata; fallback to bundled features
            const parsedFeatures = parseFeatures(c?.metadata?.features);
            const features = Array.isArray(c?.features) && c.features.length
              ? c.features
              : (parsedFeatures.length ? parsedFeatures : PLAN_FEATURES);
            return (
              <div key={i} className={`relative rounded-2xl overflow-hidden ${baseCard}`}>
                {/* subtle background lighting */}
                <div className={`pointer-events-none absolute inset-0 ${c?.featured ? 'bg-gradient-to-br from-blue-600/20 via-transparent to-cyan-500/10' : 'bg-gradient-to-br from-blue-500/10 via-transparent to-indigo-500/10'}`}></div>
                <div className="relative p-6 md:p-8">
                  <h3 className={`text-sm font-semibold tracking-wide mb-2 ${c?.featured ? 'text-theme-primary/80' : 'text-theme-secondary'}`}>
                    {c?.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    {yearlyDiscountMultiplier != 1 && isYearly && (
                      <span className={`text-sm line-through ${c?.featured ? 'text-theme-primary/70' : 'text-theme-secondary'}`}>
                        ${monthlyPrice.toLocaleString()}
                      </span>)}
                    {monthlyDiscountMultiplier != 1 && !isYearly && (
                      <span className={`text-sm line-through ${c?.featured ? 'text-theme-primary/70' : 'text-theme-secondary'}`}>
                        ${monthlyPrice.toLocaleString()}
                      </span>)}
                    <span className={`text-3xl font-bold ${c?.featured ? 'text-theme-primary' : 'text-theme-primary'}`}>
                      ${isYearly ? displayPriceFormatted : displayPriceFormatted}
                    </span>
                    <span className={`text-sm ${c?.featured ? 'text-theme-primary/70' : 'text-theme-secondary'}`}>
                      /{c?.price?.recurring?.interval}
                    </span>
                  </div>

                  <p className={`text-sm mb-4 ${c?.featured ? 'text-theme-primary/80' : 'text-theme-secondary'}`}>
                    {c?.description}
                  </p>
                  <ul className="mb-6 space-y-2">
                    {features.map((feature: string, index: number) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className={`h-4 w-4 mt-0.5 ${c?.featured ? 'text-emerald-300' : 'text-green-600'}`} />
                        <span className={`text-sm ${c?.featured ? 'text-theme-primary/80' : 'text-theme-secondary'}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Active subscription/trial notification */}
                  {userData && (userData.subscriptionStatus === 'active' ||
                    (userData.subscriptionStatus !== 'trialing' && !userData.isTrial && userData.planActive && userData.planInterval === c?.price?.recurring?.interval)) && (
                      <div className={`p-3 rounded-lg mb-4 ${theme === 'dark' ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'} border`}>
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center mr-2 ${theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'}`}>
                            <span className="text-theme-primary text-xs">✓</span>
                          </div>

                          <span className={`text-xs font-medium ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                            {userData.subscriptionStatus === 'active' ? 'Active Subscription' : 'Trial Active'}
                            {userData.planInterval && userData.subscriptionStatus === 'active' &&
                              userData.planInterval === c?.price?.recurring?.interval && ' - Current Plan'
                            }
                          </span>
                        </div>
                      </div>
                    )}

                  {(() => {
                    const buttonState = getButtonState(c);
                    return (
                      <button
                        className={
                          `w-full py-2.5 px-4 rounded-md text-sm font-medium border transition-colors btn ${buttonState.disabled
                            ? 'bg-gray-500 border-gray-500 text-theme-secondary cursor-not-allowed'
                            : buttonState.isUpgrade
                              ? 'bg-green-600 border-green-600 text-theme-primary hover:bg-green-700 hover:border-green-700'
                              : `${theme === 'dark' ? 'text-theme-primary ' : ''} active-bg border-current/30 hover:border-current/60`
                          }`
                        }
                        onClick={() => {
                          if (buttonState.disabled) return;

                          const productWithPricing = {
                            ...c,
                            selectedBilling: billing,
                            displayPrice: isYearly ? yearlyPrice : discountedMonthlyPrice,
                            effectiveMonthlyPrice: isYearly ? (yearlyPrice / 12) : discountedMonthlyPrice,
                            originalPrice: monthlyPrice
                          };
                          handleSubscription(productWithPricing);
                        }}
                        disabled={buttonState.disabled}
                      >
                        {buttonState.text}
                      </button>
                    );
                  })()}

                  {showCheckout && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                      <div className={`${theme === 'dark' ? 'bg-theme-primary' : 'bg-white'} rounded-lg shadow-2xl max-w-4xl w-full relative flex flex-col lg:flex-row max-h-[90vh] overflow-hidden`}>
                        <button
                          className={`absolute top-4 right-4 z-10 ${theme === 'dark' ? 'text-theme-muted hover:text-theme-primary' : 'text-gray-600 hover:text-gray-900'} text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-500/20`}
                          onClick={() => setShowCheckout(false)}
                          aria-label="Close"
                        >
                          ×
                        </button>
                        {/* Product Details - Right Side */}
                        <div className={`lg:w-1/2 p-6 ${theme === 'dark' ? 'bg-theme-secondary border-r border-theme-tertiary' : 'bg-gray-50 border-r border-gray-200'}`}>
                          <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                            Subscription Summary
                          </h3>
                          <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-theme-tertiary' : 'bg-white'} border ${theme === 'dark' ? 'border-theme-tertiary' : 'border-gray-200'} mb-6`}>
                            <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                              {selectedProduct?.name}
                            </h4>
                            <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-600'}`}>
                              {selectedProduct?.description}
                            </p>
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className={`text-sm ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-600'}`}>
                                  {selectedProduct?.selectedBilling === 'year' ? 'Annual Subscription' : 'Monthly Subscription'}
                                </span>
                                {discount && discount > 0 ? (
                                  <span className={`text-sm line-through ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-500'}`}>
                                    ${parseFloat(price).toFixed(2)}
                                    {selectedProduct?.selectedBilling === 'year' && '/year'}
                                    {selectedProduct?.selectedBilling === 'month' && '/month'}
                                  </span>
                                ) : (
                                  price && (
                                    <span className={`text-lg font-bold ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                                      ${parseFloat(price).toFixed(2)}
                                      {selectedProduct?.selectedBilling === 'year' && '/year'}
                                      {selectedProduct?.selectedBilling === 'month' && '/month'}
                                    </span>)
                                )}
                              </div>

                              {discount && discount > 0 && (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                                      After {discount}% Discount
                                    </span>
                                    <span className={`text-lg font-bold ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                                      ${parseFloat(afterDiscountPrice).toFixed(2)}
                                      {selectedProduct?.selectedBilling === 'year' && '/year'}
                                      {selectedProduct?.selectedBilling === 'month' && '/month'}
                                    </span>
                                  </div>
                                  <div className={`p-2 rounded-md text-center ${theme === 'dark' ? 'bg-green-900/20 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
                                    <span className={`text-xs font-medium ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                                      🎉 You saved ${(parseFloat(price) - parseFloat(afterDiscountPrice)).toFixed(2)} ({discount}% off)
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mt-6">
                            <h5 className={`font-semibold mb-3 ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                              What's included:
                            </h5>
                            <ul className="space-y-2">
                              {modalFeatures.map((feature: string, index: number) => (
                                <li key={index} className="flex items-center">
                                  <div className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${theme === 'dark' ? 'bg-green-600' : 'bg-green-500'}`}>
                                    <span className="text-theme-primary text-xs">✓</span>
                                  </div>
                                  <span className={`text-sm ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-600'}`}>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        {/* Checkout Form - Left Side */}
                        <div className="lg:w-1/2 p-6 overflow-y-auto">
                          <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`}>
                            Payment Method
                          </h3>
                          {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                              <div className="animate-spin w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full mb-4"></div>
                              <p className={`text-sm ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-600'}`}>
                                Setting up your payment...
                              </p>
                            </div>
                          ) : (
                            <CheckoutProvider
                              stripe={stripePromise}
                              options={{
                                fetchClientSecret,
                                elementsOptions: { appearance }
                              }}
                            >
                              <CheckoutForm onClose={() => {
                                setShowCheckout(false);
                                setClientSecret(null); // Reset on close
                              }} />
                            </CheckoutProvider>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Features Showcase (reused image carousel from Hero) */}
    {false && (
      <section className="py-14 md:py-16 bg-theme-primary">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-theme-primary mb-6 text-center">What you get with Stock Fortress</h2>
          <p className="text-theme-secondary text-center max-w-3xl mx-auto mb-10">A quick tour of the core capabilities.</p>

          {/* Reused carousel */}
          <div className="relative max-w-4xl mx-auto">
            <div className="relative w-full rounded-xl border border-theme-tertiary bg-theme-secondary/60 p-2.5 overflow-hidden">
              {/* Badge with marketing caption */}
              <div className="absolute top-2 left-2 z-10 rounded-md bg-emerald-500/20 px-2 py-1 text-emerald-300 text-xs flex items-center ">
                <CheckCircle2 className="h-1.5 w-1.5" />
                {getCaption(galleryImages[currentSlide]?.src || '', galleryImages[currentSlide]?.feature || 'Insight')}
              </div>

              {/* Carousel viewport */}
              <div
                ref={carouselRef}
                className="relative w-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth gap-0 scrollbar-hide"
                style={{ scrollbarWidth: 'none' as any }}
              >
                {galleryImages.map((g) => (
                  <div key={g.src} className="flex-shrink-0 w-full snap-start">
                    <div className="relative w-full aspect-[16/9]">
                      <img
                        src={g.src}
                        alt={getAlt(g.src, g.feature)}
                        className="absolute inset-0 w-full h-full object-contain rounded-lg "
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Controls */}
              {galleryImages.length > 1 && (
                <div className="absolute bottom-2 right-2 flex items-center gap-2 z-10">
                  <button onClick={prev} aria-label="Previous" className="rounded-full bg-theme-secondary/80 border border-theme-tertiary p-1.5 text-theme-primary hover:bg-theme-tertiary">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button onClick={next} aria-label="Next" className="rounded-full bg-theme-secondary/80 border border-theme-tertiary p-1.5 text-theme-primary hover:bg-theme-tertiary">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/*
          Original GIF grid commented out per request to reuse the image carousel for now.
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"> ... </div>
        */}
        </div>
      </section>
    )}

    {/* FAQ Section */}
    <section className="py-14 bg-theme-primary">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-theme-primary mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-5">
            <div className="bg-theme-secondary rounded-lg p-5 border border-theme-tertiary">
              <h3 className="text-base font-semibold text-theme-primary mb-2">
                Can I upgrade or downgrade my plan?
              </h3>
              <p className="text-sm text-theme-secondary">
                Yes, you can upgrade or downgrade your plan at any time. When
                upgrading, you'll be charged the prorated difference. When
                downgrading, the new rate will apply at the start of your next
                billing cycle.
              </p>
            </div>
            <div className="bg-theme-secondary rounded-lg p-5 border border-theme-tertiary">
              <h3 className="text-base font-semibold text-theme-primary mb-2">
                Is there a free trial?
              </h3>
              <p className="text-sm text-theme-secondary">
                Currently, we don't offer a free trial. However, you can cancel your subscription at any time if you're not satisfied with our service.
              </p>
            </div>
            <div className="bg-theme-secondary rounded-lg p-5 border border-theme-tertiary">
              <h3 className="text-base font-semibold text-theme-primary mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-sm text-theme-secondary">
                We accept major credit cards including Visa, Mastercard,
                American Express, and Discover. For Enterprise plans, we also
                support invoicing and bank transfers.
              </p>
            </div>
            <div className="bg-theme-secondary rounded-lg p-5 border border-theme-tertiary">
              <h3 className="text-base font-semibold text-theme-primary mb-2">
                Can I cancel my subscription?
              </h3>
              <p className="text-sm text-theme-secondary">
                Yes, you can cancel your subscription at any time. When you
                cancel, you'll retain access to your plan until the end of
                your current billing period.
              </p>
            </div>
            <div className="bg-theme-secondary rounded-lg p-5 border border-theme-tertiary">
              <h3 className="text-base font-semibold text-theme-primary mb-2">
                Do you offer discounts for students or educational
                institutions?
              </h3>
              <p className="text-sm text-theme-secondary">
                Yes, we offer special pricing for students, educators, and
                academic institutions. Please contact our support team with
                valid academic credentials to learn more.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>;
}